import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../../components/BackButton';

import Header from '../../components/ui/Header';

import { supabase } from "../../lib/supabase";
import hrService from "../../services/hrService";
import AttendanceFilters from "./components/AttendanceFilters";
import AttendanceSummary from './components/AttendanceSummary';
import AttendanceUpload from './components/AttendanceUpload';
import EmployeeAttendanceTable from './components/EmployeeAttendanceTable';
import EditAttendanceModal from './components/EditAttendanceModal';

const AttendanceTracking = () => {
  const { userProfile } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    employeeId: null,
    filterType: "all"
  });
  const [editingRecord, setEditingRecord] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [clearingRecords, setClearingRecords] = useState(false);

  // Determine dashboard route based on user role
  const getDashboardRoute = () => {
    const role = userProfile?.role;
    if (role === 'admin' || role === 'hr_manager') {
      return '/admin-dashboard';
    }
    return '/employee-dashboard';
  };

  // Apply filters to attendance records - moved before handleFilterChange
  const applyFilters = useCallback((filters) => {
    console.log('🔍 applyFilters called with:', filters);
    console.log('📊 Total attendance records:', attendanceRecords?.length);
    
    if (!attendanceRecords || attendanceRecords?.length === 0) {
      setFilteredRecords([]);
      return;
    }

    let filtered = [...attendanceRecords];

    // Filter by employee
    if (filters?.employeeId) {
      console.log('👤 Filtering by employee ID:', filters?.employeeId);
      filtered = filtered?.filter(record => record?.employeeId === filters?.employeeId);
      console.log('📊 After employee filter:', filtered?.length, 'records');
      
      // Debug: Show employee codes in filtered records
      const employeeCodes = filtered?.map(r => r?.employees?.employeeId)?.filter(Boolean);
      console.log('📋 Employee codes in filtered records:', [...new Set(employeeCodes)]);
    }

    // Filter by date range or monthly
    if (filters?.filterType === "dateRange" || filters?.filterType === "monthly") {
      if (filters?.startDate && filters?.endDate) {
        const start = new Date(filters.startDate);
        start?.setHours(0, 0, 0, 0); // Start of day
        
        const end = new Date(filters.endDate);
        end?.setHours(23, 59, 59, 999); // End of day to include full last day
        
        console.log('📅 Date Filter Debug:', {
          filterType: filters?.filterType,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          startObj: start?.toISOString(),
          endObj: end?.toISOString(),
          recordsBeforeFilter: filtered?.length
        });
        
        // Debug: Show date range in data
        const dates = filtered?.map(r => r?.attendanceDate)?.sort();
        console.log('📅 Date range in data BEFORE filter:', {
          first: dates?.[0],
          last: dates?.[dates?.length - 1],
          total: dates?.length
        });
        
        filtered = filtered?.filter(record => {
          const recordDate = new Date(record?.attendanceDate);
          const isInRange = recordDate >= start && recordDate <= end;
          
          return isInRange;
        });
        
        // Debug: Show date range AFTER filter
        const filteredDates = filtered?.map(r => r?.attendanceDate)?.sort();
        console.log('📅 Date range in data AFTER filter:', {
          first: filteredDates?.[0],
          last: filteredDates?.[filteredDates?.length - 1],
          total: filteredDates?.length
        });
        
        console.log('✅ Filtered records count:', filtered?.length);
      }
    }

    setFilteredRecords(filtered);
  }, [attendanceRecords]);

  // Memoized filter change handler - now includes applyFilters in dependencies
  const handleFilterChange = useCallback((filters) => {
    setActiveFilters(filters);
    applyFilters(filters);
  }, [applyFilters]);

  // ENHANCED: Load data function with better debugging
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Starting data load...');
      
      // Fetch employees using proper hrService method
      const employeesData = await hrService?.getAllEmployees();
      console.log('✅ Employees loaded:', employeesData?.length);
      console.log('📊 First employee structure:', employeesData?.[0]);
      setEmployees(employeesData || []);
      
      // Fetch attendance records using proper hrService method
      const attendanceData = await hrService?.getAttendanceRecords();
      console.log('✅ Attendance records loaded:', attendanceData?.length);
      console.log('📊 First attendance record structure:', attendanceData?.[0]);
      console.log('📊 Attendance employee IDs:', attendanceData?.map(r => r?.employeeId)?.slice(0, 5));
      
      // CRITICAL DEBUGGING: Count all status types in raw data
      if (attendanceData && attendanceData?.length > 0) {
        const statusCounts = {};
        attendanceData?.forEach(record => {
          const status = record?.attendanceStatus || record?.attendance_status || 'unknown';
          statusCounts[status] = (statusCounts?.[status] || 0) + 1;
        });
        
        console.log('📊 STATUS DISTRIBUTION IN RAW DATA:', statusCounts);
        console.log('🎯 HALF DAY CHECK: Does data contain "halfday"?', Object.keys(statusCounts)?.includes('halfday'));
        console.log('🎯 HALF DAY CHECK: Does data contain "half_day"?', Object.keys(statusCounts)?.includes('half_day'));
        console.log('🎯 HALF DAY CHECK: Does data contain "halfDay"?', Object.keys(statusCounts)?.includes('halfDay'));
        
        // Log some half_day records if they exist
        const halfDayRecords = attendanceData?.filter(r => 
          (r?.attendanceStatus === 'halfday' || r?.attendance_status === 'halfday' ||
           r?.attendanceStatus === 'half_day' || r?.attendance_status === 'half_day' ||
           r?.attendanceStatus === 'halfDay' || r?.attendance_status === 'halfDay')
        );
        
        if (halfDayRecords?.length > 0) {
          console.log('✅ Found half day records:', halfDayRecords?.length);
          console.log('Sample half day record:', halfDayRecords?.[0]);
        } else {
          console.error('❌ NO HALF DAY RECORDS FOUND IN DATABASE RESPONSE');
          console.log('All unique status values in response:', 
            [...new Set(attendanceData?.map(r => r?.attendanceStatus || r?.attendance_status))]);
        }
      }
      
      setAttendanceRecords(attendanceData || []);
      
      // CRITICAL DEBUGGING: Check if employee IDs match
      if (employeesData?.length > 0 && attendanceData?.length > 0) {
        const employeeIds = new Set(employeesData?.map(e => e?.id));
        const attendanceEmployeeIds = new Set(attendanceData?.map(r => r?.employeeId));
        
        console.log('🔗 Employee IDs in employees table:', Array.from(employeeIds)?.slice(0, 5));
        console.log('🔗 Employee IDs in attendance records:', Array.from(attendanceEmployeeIds)?.slice(0, 5));
        
        const matchingIds = Array.from(attendanceEmployeeIds)?.filter(id => employeeIds?.has(id));
        console.log(`✅ Matching employee IDs: ${matchingIds?.length}/${attendanceData?.length}`);
        
        if (matchingIds?.length === 0) {
          console.error('❌ CRITICAL: No matching employee IDs between employees and attendance records!');
          setError('Data mismatch: Attendance records exist but employee IDs do not match. Please contact support.');
        }
      }
      
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError(err?.message || 'Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters whenever records change
  useEffect(() => {
    applyFilters(activeFilters);
  }, [attendanceRecords, applyFilters, activeFilters]);

  // Add this function
  const handleManualRefresh = async () => {
    await loadData();
  };

  const handleUploadSuccess = async (uploadResult) => {
    console.log('Upload completed:', uploadResult);
    
    if (uploadResult?.recordsProcessed > 0) {
      // Trigger manual refresh after upload
      handleManualRefresh();
    }
  };

  const handleEdit = async (record) => {
    setEditingRecord(record);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;
    
    try {
      const { error } = await supabase?.from('attendance_records')?.delete()?.eq('id', recordId);
      if (error) throw error;
      await loadData(); // Refresh data after delete
      setSuccessMessage('Record deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to delete record');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleSave = async (updatedRecord) => {
    try {
      const { error } = await supabase?.from('attendance_records')?.update(updatedRecord)?.eq('id', updatedRecord?.id);
      if (error) throw error;
      await loadData(); // Refresh data after update
      setEditingRecord(null); // Close modal
      setSuccessMessage('Record updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to update record');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Add new function to clear all attendance records
  const handleClearAllRecords = async () => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete ALL ${attendanceRecords?.length} attendance records from the database.\n\nThis action CANNOT be undone!\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput !== 'DELETE ALL') {
      if (userInput !== null) {
        alert('Clear operation cancelled. You must type "DELETE ALL" to confirm.');
      }
      return;
    }

    try {
      setClearingRecords(true);
      setError(null);
      setErrorMessage('');

      // CRITICAL FIX 1: Clear state BEFORE making the API call
      const recordCountBeforeClear = attendanceRecords?.length || 0;
      setAttendanceRecords([]);
      setFilteredRecords([]);

      // Call the Supabase function to clear all records
      const { data, error: clearError } = await supabase?.rpc('clear_all_attendance_records');

      if (clearError) {
        console.error('❌ Clear operation error:', clearError);
        // CRITICAL FIX 2: Reload data if operation fails to restore previous state
        await loadData();
        throw clearError;
      }

      const result = data?.[0];
      console.log('✅ Clear operation result:', result);
      
      // CRITICAL FIX 3: Show success message regardless of count
      // (empty table is a valid success state)
      if (result) {
        setSuccessMessage(
          result?.deleted_count > 0 
            ? `✅ Successfully deleted ${result?.deleted_count} attendance records` 
            : '✅ No records to delete - table is already empty'
        );
      } else {
        setSuccessMessage(`✅ Clear operation completed`);
      }

      // CRITICAL FIX 4: Don't reload data - state is already cleared
      // This prevents a race condition where old data briefly reappears
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('❌ Error clearing attendance records:', err);
      setErrorMessage(err?.message || 'Failed to clear attendance records');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setClearingRecords(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendance records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back Button */}
        <div className="mb-8">
          <BackButton 
            to={getDashboardRoute()} 
            label="Back to Dashboard" 
            className="mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Attendance Tracking
          </h1>
          <p className="text-gray-600">
            Monitor and manage employee attendance records
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {(error || errorMessage) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error || errorMessage}</p>
          </div>
        )}

        {/* DEBUGGING INFO - Remove after fixing */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-semibold mb-2">Debug Info:</p>
            <p className="text-blue-700 text-sm">Employees: {employees?.length}</p>
            <p className="text-blue-700 text-sm">Attendance Records: {attendanceRecords?.length}</p>
            <p className="text-blue-700 text-sm">Filtered Records: {filteredRecords?.length}</p>
          </div>
        )}

        {/* Filters */}
        <AttendanceFilters onFilterChange={handleFilterChange} />

        {/* Summary */}
        <AttendanceSummary records={filteredRecords} summaryData={filteredRecords} />

        {/* Upload and Clear Controls */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1 w-full">
              <AttendanceUpload onUploadSuccess={handleUploadSuccess} onUploadComplete={handleUploadSuccess} />
            </div>
            
            {/* Clear All Records Button - Only show for admin/hr_manager */}
            {(userProfile?.role === 'admin' || userProfile?.role === 'hr_manager') && attendanceRecords?.length > 0 && (
              <div className="w-full sm:w-auto">
                <button
                  onClick={handleClearAllRecords}
                  disabled={clearingRecords || attendanceRecords?.length === 0}
                  className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {clearingRecords ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Clearing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Clear All Records</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center sm:text-left">
                  ⚠️ Permanently deletes all {attendanceRecords?.length} records
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Employee Attendance Table - NEW */}
        <div className="mb-6">
          <EmployeeAttendanceTable 
            employees={employees}
            attendanceRecords={filteredRecords}
            selectedEmployeeId={activeFilters?.employeeId}
            selectedEmployeeName={
              activeFilters?.employeeId 
                ? employees?.find(e => e?.id === activeFilters?.employeeId)?.userProfiles?.full_name 
                  || employees?.find(e => e?.id === activeFilters?.employeeId)?.name 
                  || 'Selected Employee'
                : null
            }
          />
        </div>

        {/* Edit Modal */}
        {editingRecord && (
          <EditAttendanceModal
            record={editingRecord}
            isOpen={!!editingRecord}
            onClose={() => setEditingRecord(null)}
            onSave={handleSave}
          />
        )}
      </div>
    </div>
  );
};

export default AttendanceTracking;